/**
 * Tiled poster PDF from a single image or PDF page, built with pdf-lib.
 *
 * The source is embedded ONCE and drawn shifted onto every A4 sheet; each
 * sheet's MediaBox clips away whatever lies outside it. No canvas is ever
 * created for the whole poster (iPad memory), and a PDF page stays vector.
 */

import {
  degrees,
  PDFDocument,
  type PDFEmbeddedPage,
  type PDFImage,
  type PDFPage,
  rgb,
} from "pdf-lib";
import { MM_TO_PT } from "./paper.js";
import type { PosterPlan, PosterSheet } from "./poster-grid.js";
import {
  MARK_STYLE,
  type MarkGlyph,
  type MarkLine,
  type SheetMarkGeometry,
  sheetMarkGeometry,
} from "./sheet-mark-geometry.js";

/** Input the user can fix by picking another file; `message` is UI text. */
export class PosterInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PosterInputError";
  }
}

export const POSTER_PDF_PROTECTED_MESSAGE =
  "Diese PDF ist geschützt und kann nicht als Poster gedruckt werden.";

const POSTER_PDF_NO_AREA_MESSAGE = "Diese PDF-Seite hat keine druckbare Fläche.";

export type PdfRotation = 0 | 90 | 180 | 270;

export function normalizeRotation(angle: number): PdfRotation {
  return ((((Math.round(angle / 90) * 90) % 360) + 360) % 360) as PdfRotation;
}

/** Size a viewer shows: a quarter turn swaps the crop box's sides. */
export function pdfPageDisplaySize(
  cropW: number,
  cropH: number,
  rotation: PdfRotation,
): { w: number; h: number } {
  return rotation === 90 || rotation === 270 ? { w: cropH, h: cropW } : { w: cropW, h: cropH };
}

/** PDF coordinates, y pointing up. */
export interface PtRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Arguments for pdf-lib `drawPage` / `drawImage` so that the object, turned
 * clockwise by `rotation` like a PDF `/Rotate`, exactly fills `rect`. pdf-lib
 * rotates counter-clockwise about (x, y), hence the negated angle.
 */
export function placeRotated(rect: PtRect, rotation: PdfRotation): PtRect & { rotateDeg: number } {
  const { x, y, width, height } = rect;
  switch (rotation) {
    case 90:
      return { x, y: y + height, width: height, height: width, rotateDeg: -90 };
    case 180:
      return { x: x + width, y: y + height, width, height, rotateDeg: 180 };
    case 270:
      return { x: x + width, y, width: height, height: width, rotateDeg: 90 };
    default:
      return { x, y, width, height, rotateDeg: 0 };
  }
}

/** Where the whole poster lies on `sheet`, in that sheet's PDF coordinates. */
export function posterRectOnSheetPt(sheet: PosterSheet, plan: PosterPlan): PtRect {
  return {
    x: -sheet.originXMm * MM_TO_PT,
    y: (plan.grid.sheetHMm + sheet.originYMm - plan.posterHMm) * MM_TO_PT,
    width: plan.posterWMm * MM_TO_PT,
    height: plan.posterHMm * MM_TO_PT,
  };
}

/**
 * Load a PDF the user picked. Shared with the poster dialog's inspection so
 * both report unreadable and protected files with the same text.
 */
export async function loadPosterSourcePdf(bytes: Uint8Array): Promise<PDFDocument> {
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  } catch {
    throw new PosterInputError("PDF konnte nicht gelesen werden.");
  }
  if (doc.isEncrypted) throw new PosterInputError(POSTER_PDF_PROTECTED_MESSAGE);
  return doc;
}

export interface PdfPageGeometry {
  /** Visible area in the page's own (unrotated) coordinates. */
  crop: PtRect;
  rotation: PdfRotation;
  /** Size as a viewer shows it, in pt. */
  display: { w: number; h: number };
}

/** Finite and > 0 — a usable size. */
export function isPositiveFinite(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}

/**
 * CropBox and `/Rotate` of a page — what a viewer shows. Throws for a page
 * without area, so the aspect ratio derived from `display` is always finite
 * and positive.
 */
export function pdfPageGeometry(page: PDFPage): PdfPageGeometry {
  const crop = page.getCropBox();
  const rotation = normalizeRotation(page.getRotation().angle);
  const display = pdfPageDisplaySize(crop.width, crop.height, rotation);
  if (!isPositiveFinite(display.w) || !isPositiveFinite(display.h)) {
    throw new PosterInputError(POSTER_PDF_NO_AREA_MESSAGE);
  }
  return {
    crop: { x: crop.x, y: crop.y, width: crop.width, height: crop.height },
    rotation,
    display,
  };
}

export type PosterPdfSource =
  | { kind: "jpg" | "png"; bytes: Uint8Array }
  | { kind: "pdf"; bytes: Uint8Array; pageIndex: number };

export interface PosterPdfAssets {
  assemblyPng: Uint8Array | null;
  scissorsPng: Uint8Array | null;
  gluePng: Uint8Array | null;
}

const ink = (c: readonly [number, number, number, number]) => ({
  color: rgb(c[0] / 255, c[1] / 255, c[2] / 255),
  opacity: c[3],
});

/**
 * Draw the assembly marks of one sheet. Geometry is in mm with y down from
 * the sheet's top edge; PDF wants pt with y up from the bottom edge.
 */
export function drawSheetMarksPdf(
  page: PDFPage,
  geom: SheetMarkGeometry,
  sheetHMm: number,
  glyphs: { scissors: PDFImage | null; glue: PDFImage | null },
): void {
  const yPt = (yMm: number) => (sheetHMm - yMm) * MM_TO_PT;

  const tint = ink(MARK_STYLE.glueTint);
  for (const r of geom.glueTint) {
    page.drawRectangle({
      x: r.xMm * MM_TO_PT,
      y: yPt(r.yMm + r.hMm),
      width: r.wMm * MM_TO_PT,
      height: r.hMm * MM_TO_PT,
      borderWidth: 0,
      ...tint,
    });
  }

  const line = (
    l: MarkLine,
    style: {
      c: readonly [number, number, number, number];
      widthMm: number;
      dashMm: readonly number[];
    },
  ) => {
    page.drawLine({
      start: { x: l.x1Mm * MM_TO_PT, y: yPt(l.y1Mm) },
      end: { x: l.x2Mm * MM_TO_PT, y: yPt(l.y2Mm) },
      thickness: style.widthMm * MM_TO_PT,
      dashArray: style.dashMm.map((d) => d * MM_TO_PT),
      ...ink(style.c),
    });
  };
  for (const l of geom.glueLines) {
    line(l, {
      c: MARK_STYLE.glueInk,
      widthMm: MARK_STYLE.glueLineWidthMm,
      dashMm: MARK_STYLE.glueDashMm,
    });
  }
  for (const l of geom.cutLines) {
    line(l, {
      c: MARK_STYLE.cutInk,
      widthMm: MARK_STYLE.cutLineWidthMm,
      dashMm: MARK_STYLE.cutDashMm,
    });
  }

  const glyph = (img: PDFImage, g: MarkGlyph, opacity?: number) => {
    // The box as it appears on paper: a vertical glyph is turned a quarter.
    const wMm = g.vertical ? g.hMm : g.wMm;
    const hMm = g.vertical ? g.wMm : g.hMm;
    const box: PtRect = {
      x: (g.cxMm - wMm / 2) * MM_TO_PT,
      y: yPt(g.cyMm + hMm / 2),
      width: wMm * MM_TO_PT,
      height: hMm * MM_TO_PT,
    };
    const { rotateDeg, ...placed } = placeRotated(box, g.vertical ? 90 : 0);
    page.drawImage(img, {
      ...placed,
      rotate: degrees(rotateDeg),
      ...(opacity === undefined ? {} : { opacity }),
    });
  };
  if (glyphs.glue) {
    for (const g of geom.glueGlyphs) glyph(glyphs.glue, g, MARK_STYLE.glueGlyphOpacity);
  }
  if (glyphs.scissors) {
    for (const g of geom.scissors) glyph(glyphs.scissors, g);
  }
}

export async function buildPosterPdf(args: {
  source: PosterPdfSource;
  plan: PosterPlan;
  assets: PosterPdfAssets;
}): Promise<Uint8Array> {
  const { source, plan, assets } = args;
  const doc = await PDFDocument.create();

  // Embedded once; every sheet draws the same object.
  let drawPoster: (page: PDFPage, rect: PtRect) => void;
  if (source.kind === "pdf") {
    const srcDoc = await loadPosterSourcePdf(source.bytes);
    if (
      !Number.isInteger(source.pageIndex) ||
      source.pageIndex < 0 ||
      source.pageIndex >= srcDoc.getPageCount()
    ) {
      throw new PosterInputError("Seite nicht gefunden.");
    }
    const srcPage = srcDoc.getPage(source.pageIndex);
    const { crop, rotation } = pdfPageGeometry(srcPage);
    // A blank page has no content stream at all and pdf-lib refuses to embed
    // it (its error class fails `instanceof` in the ES5 build, so check first).
    if (!srcPage.node.Contents()) throw new PosterInputError(POSTER_PDF_NO_AREA_MESSAGE);
    const embedded: PDFEmbeddedPage = await doc.embedPage(srcPage, {
      left: crop.x,
      bottom: crop.y,
      right: crop.x + crop.width,
      top: crop.y + crop.height,
    });
    drawPoster = (page, rect) => {
      const { rotateDeg, ...placed } = placeRotated(rect, rotation);
      page.drawPage(embedded, { ...placed, rotate: degrees(rotateDeg) });
    };
  } else {
    const image =
      source.kind === "jpg" ? await doc.embedJpg(source.bytes) : await doc.embedPng(source.bytes);
    drawPoster = (page, rect) => page.drawImage(image, rect);
  }

  const { sheetWMm, sheetHMm } = plan.grid;
  const sheetSizePt: [number, number] = [sheetWMm * MM_TO_PT, sheetHMm * MM_TO_PT];

  const hasMarks = plan.sheets.some((s) => s.marks);
  const glyphs = {
    scissors: hasMarks && assets.scissorsPng ? await doc.embedPng(assets.scissorsPng) : null,
    glue: hasMarks && assets.gluePng ? await doc.embedPng(assets.gluePng) : null,
  };

  for (const sheet of plan.sheets) {
    const page = doc.addPage(sheetSizePt);
    drawPoster(page, posterRectOnSheetPt(sheet, plan));
    if (sheet.marks) {
      drawSheetMarksPdf(page, sheetMarkGeometry(sheet.marks, sheetWMm, sheetHMm), sheetHMm, glyphs);
    }
  }

  // Assembly guide as the final page, edge to edge like `to-pdf.ts`.
  if (assets.assemblyPng) {
    const guide = await doc.embedPng(assets.assemblyPng);
    const page = doc.addPage(sheetSizePt);
    page.drawImage(guide, { x: 0, y: 0, width: sheetSizePt[0], height: sheetSizePt[1] });
  }

  return doc.save();
}
